import { OktaAuth, OktaAuthOptions } from '@okta/okta-auth-js';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment-timezone';

const ID_TOKEN = 'id_token';
const PROFILE_EXPIRY = 'profile_expiry';
const PROFILE = 'profile';
const CURRENT_AGENCY = 'current_agency';
const CURRENT_WORKSPACE_ID = 'current_workspace_id';

export const oauthLoginCallbackURL = '/implicit/callback';
export const oauthLogoutCallbackURL = '/logout/callback';
const isNotTestEnv = process.env.ENVIRONMENT && process.env.ENVIRONMENT !== 'test';

export const ssoConfig: OktaAuthOptions = isNotTestEnv
  ? {
      issuer: process.env.SSO_ISSUER,
      redirectUri: `${process.env.APP_HOST_URL}${oauthLoginCallbackURL}`,
      clientId: process.env.SSO_CLIENT_ID,
      // tslint:disable-next-line: max-line-length
      postLogoutRedirectUri: `${process.env.APP_HOST_URL}${oauthLogoutCallbackURL}`,
      pkce: true,
      scopes: ['openid', 'profile', 'email', 'custom_poc'],
    }
  : {
      issuer: 'https://{yourOktaDomain}.com/oauth2/{authServerId}',
      redirectUri: 'https://{appHostURL}/implicit/callback',
      postLogoutRedirectUri: 'https://{appHostURL}/logout/callback',
      clientId: 'SSO_CLIENT_ID',
      pkce: true,
      scopes: ['openid', 'profile', 'email', 'custom_poc'],
    };

export const authService = new OktaAuth(ssoConfig);

export const saveCurrentAgency = (currentAgency: IAgencyResponse) => {
  localStorage.setItem(CURRENT_AGENCY, JSON.stringify(currentAgency));
};

export const setWorkspaceId = (workspaceId: number) => {
  if (workspaceId) {
    localStorage.setItem(CURRENT_WORKSPACE_ID, workspaceId.toString());
  }
};

export const removeWorkspaceId = () => localStorage.removeItem(CURRENT_WORKSPACE_ID);

export const getWorkspaceId = () => {
  const workspaceId = localStorage.getItem(CURRENT_WORKSPACE_ID);

  return workspaceId && +workspaceId;
};

export const getCurrentAgency = (): IAgencyResponse => JSON.parse(localStorage.getItem(CURRENT_AGENCY));

export const storeUserProfile = (jwtToken: string) => {
  const userProfile = jwtDecode<IUserProfile>(jwtToken);

  localStorage.setItem(ID_TOKEN, jwtToken);
  localStorage.setItem(PROFILE, JSON.stringify(userProfile));
  localStorage.setItem(PROFILE_EXPIRY, userProfile.exp.toString());

  return userProfile;
};

export const userLogOut = (shouldRedirect: boolean = false) => {
  let agencyId;
  if (shouldRedirect) {
    agencyId = getUserProfile()?.agencyId ?? 1; // login auto redirect to correct agency
  }

  localStorage.removeItem(ID_TOKEN);
  localStorage.removeItem(PROFILE);
  localStorage.removeItem(PROFILE_EXPIRY);
  sessionStorage.removeItem('selectedFilters');
  sessionStorage.removeItem('selectedEntities');
  sessionStorage.removeItem('updatedCategories');
  sessionStorage.removeItem('selectedFilterName');
  if (shouldRedirect) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.location = `/agencies/${agencyId}/login`;
  }
};

export const oauthUserLogout = () => {
  localStorage.removeItem(CURRENT_AGENCY);
};

export const isLoggedIn = () => {
  const expiry = localStorage?.getItem(PROFILE_EXPIRY);
  const userProfile = localStorage?.getItem(PROFILE);
  const timezone = getUserProfile() ? getUserProfile().agencyTimezone : null;

  return expiry && userProfile
    ? timezone
      ? moment().tz(timezone).unix() < +expiry
      : moment().unix() < +expiry
    : false;
};

export const getUserProfile = (): IUserProfile => JSON.parse(localStorage?.getItem(PROFILE));

export const getIdToken = () => localStorage.getItem(ID_TOKEN);

export const hasComcateAdminAccess = (userProfile: IUserProfile): boolean =>
  (!!userProfile && !!userProfile.scopes && userProfile.scopes.comcateAdmin) || false;

export const hasSiteAdminAccess = (userProfile: IUserProfile): boolean =>
  !!userProfile && !!userProfile.scopes && (userProfile.scopes.superAdmin || userProfile.scopes.siteAdmin);

export const hasUserAdminAccess = (userProfile: IUserProfile): boolean =>
  !!userProfile && !!userProfile.scopes && (userProfile.scopes.superAdmin || userProfile.scopes.userAdmin);

export const hasProductAdminAccess = (product: ConfigProductType, userProfile: IUserProfile): boolean =>
  !!userProfile && !!userProfile.scopes && userProfile.scopes[product] && userProfile.scopes[product].isActive
    ? userProfile.scopes.superAdmin || userProfile.scopes[product].productAdmin
    : false;

export const hasProductCEAccess = (userProfile: IUserProfile): boolean =>
  !!userProfile && !!userProfile.scopes && !!userProfile.scopes.xerce && userProfile.scopes.xerce.isActive;

export const hasProductCRMAccess = (userProfile: IUserProfile): boolean =>
  !!userProfile && !!userProfile.scopes && !!userProfile.scopes.crm && userProfile.scopes.crm.isActive;

export const hasActionCenterAccess = (userProfile: IUserProfile): boolean =>
  !!userProfile && userProfile.hasActionCenterAccess && hasInternalSubmissionAccess(userProfile);

export const hasCEExecutiveReportsAccess = (userProfile: IUserProfile): boolean =>
  !!userProfile &&
  !!userProfile.scopes &&
  !!userProfile.scopes.xerce &&
  userProfile.scopes.xerce.isActive &&
  userProfile.scopes.xerce.hasExecutiveReportsAccess;

export const hasCRMExecutiveReportsAccess = (userProfile: IUserProfile): boolean =>
  !!userProfile &&
  !!userProfile.scopes &&
  !!userProfile.scopes.crm &&
  !!userProfile.scopes.crm.isActive &&
  userProfile.scopes.crm.hasExecutiveReportsAccess;

export const isSuperAdmin = (userProfile: IUserProfile): boolean => !!userProfile.scopes.superAdmin;

export const hasCreateCasePageAccess = (userProfile: IUserProfile): boolean => {
  const violationTypeScope = userProfile.scopes.xerce ? userProfile.scopes.xerce.violationTypeScope : {};
  const hasCreateCaseAccess = Object.keys(violationTypeScope).reduce(
    (access, violationTypeId) =>
      access ||
      (violationTypeScope[violationTypeId].isActive &&
        violationTypeScope[violationTypeId].violationTypeAccess === ViolationTypeAccess.BASIC) ||
      (violationTypeScope[violationTypeId].isActive &&
        violationTypeScope[violationTypeId].violationTypeAccess === ViolationTypeAccess.OVERWRITE),
    false
  );

  return userProfile.scopes.xerce && userProfile.scopes.xerce.isActive
    ? userProfile.scopes.superAdmin || hasCreateCaseAccess
    : false;
};

export const getViolationTypeIds = (): string[] => {
  const userProfile = getUserProfile();
  if (userProfile && userProfile.scopes && userProfile.scopes.xerce && userProfile.scopes.xerce.violationTypeScope) {
    return Object.keys(userProfile.scopes.xerce.violationTypeScope);
  }

  return [];
};

export const getUpdatedViolationTypeScope = (user: IUser): IUserProfile => {
  const userProfile = getUserProfile();
  const userId = user.id;
  const getViolationTypeScope = () => {
    const violationTypeScope = {};
    // Scope separation
    const violationTypePermissions = user.xerce.violationTypePermissions.map((e) => ({ ...e }));

    violationTypePermissions
      .sort((a, b) => a.configXerceViolationTypeId - b.configXerceViolationTypeId)
      .forEach((elem) => {
        const { isActive, violationTypeAccess } = elem;
        violationTypeScope[elem.configXerceViolationTypeId] = {
          isActive,
          violationTypeAccess,
        };
      });

    return violationTypeScope;
  };

  if (userProfile.id && userProfile.id === userId) {
    userProfile.scopes = {
      siteAdmin: user.isSiteAdmin,
      xerce: {
        dashboardAccess: user.xerce.dashboardAccess,
        hasExecutiveReportsAccess: user.xerce.hasExecutiveReportsAccess,
        noticeAccess: NoticeAccess.CANNOT_REISSUE_AND_DELETE,
        fineAccess: user.xerce.fineAccess,
        id: user.xerce.xerceId,
        isActive: user.xerce.isActive,
        productAdmin: user.xerce.isProductAdmin,
        reportAccess: user.xerce.reportAccess,
        violationTypeScope: getViolationTypeScope(),
      },
    };
    localStorage.setItem(PROFILE, JSON.stringify(userProfile));
  }

  return userProfile;
};

export const hasReportAccess = (userProfile: IUserProfile): boolean =>
  !!userProfile.scopes.xerce &&
  userProfile.scopes.xerce.isActive &&
  userProfile.scopes.xerce.reportAccess !== ReportAccess.NO_ACCESS;

export const isCustomerSubmissionReadOnly = (userProfile: IUserProfile): boolean =>
  userProfile.scopes &&
  userProfile.scopes.crm &&
  (!userProfile.scopes.crm.isActive || userProfile.scopes.crm.permission === CRMPermission.READ_ONLY);

export const hasContactEditAccess = (userProfile: IUserProfile): boolean => {
  if (!userProfile || !userProfile.scopes) {
    return false;
  }

  const crm = userProfile.scopes.crm;
  const hasCRMWriteAccess =
    !!crm && !!crm.isActive && (crm.permission === CRMPermission.BASIC || crm.permission === CRMPermission.OVERWRITE);

  return hasCRMWriteAccess || hasCreateCasePageAccess(userProfile);
};

export const hasValidCRMPermission = (permission: CRMPermission): boolean => {
  const userProfile = getUserProfile();

  return userProfile.scopes && userProfile.scopes.crm && userProfile.scopes.crm.permission === permission;
};

export const hasInternalSubmissionAccess = (userProfile: IUserProfile): boolean =>
  userProfile.scopes &&
  userProfile.scopes.crm &&
  userProfile.scopes.crm.isActive &&
  userProfile.scopes.crm.internalSubmissionCreation === CRMInternalSubmissionAccess.CAN_CREATE;

export const hasGroupsPermission = (userProfile: IUserProfile): boolean =>
  !!userProfile &&
  !!userProfile.permissionPreference &&
  userProfile.permissionPreference === PermissionPreference.GROUPS;

export const hasSupervisorAccess = (userProfile: IUserProfile): boolean =>
  userProfile.scopes &&
  userProfile.scopes.xerce &&
  (userProfile.scopes.xerce.supervisorAccess === SupervisorPermissionAccess.SUPERVISOR_BASIC ||
    userProfile.scopes.xerce.supervisorAccess === SupervisorPermissionAccess.SUPERVISOR_MANAGER);

export const hasCRMCommlinkAccess = (userProfile: IUserProfile): boolean =>
  !!userProfile &&
  !!userProfile.scopes &&
  !!userProfile.scopes.crm &&
  !!userProfile.scopes.crm.isActive &&
  userProfile.scopes.crm.hasCommlinkAccess;
